import { yupResolver } from '@hookform/resolvers/yup';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { TransactionCategoryModel } from 'api-hooks/transaction-category';
import { NavigationRoutes } from 'common/constants';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import Input from 'mantine-components/elements';
import { HalfGridContainer } from 'mantine-components/container/grid-container';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UniskaiFormHeaderRef } from 'templates/form/uniskai-form-header';
import UniskaiFormTemplate from 'templates/form/uniskai-form-template';

import {
  TransactionCategoryFormSchema,
  TransactionCategoryFormType,
} from './form-type';
import useTransactionCategoryDefaultValues from '../hooks/use-transaction-category-default-values';

interface TransactionCategoryFormProps {
  query?: UseQueryResult<ApiResult<TransactionCategoryModel>, ApiError>;
  onSubmit: (
    input: TransactionCategoryFormType,
    form: UseFormReturn<TransactionCategoryFormType>,
  ) => Promise<TransactionCategoryModel>;
  saveMutation?: UseMutationResult;
}

export default function TransactionCategoryForm(
  props: TransactionCategoryFormProps,
) {
  const { t } = useTranslation();
  const transactionCategory = props.query?.data?.data;
  const defaultValues =
    useTransactionCategoryDefaultValues(transactionCategory);
  const { deleteMutate, onDelete } = useDestroyAction('transaction-categories');
  const permission = useGetCrudPermission('transaction-categories');
  const titleRef = React.useRef<UniskaiFormHeaderRef>(null);

  React.useEffect(() => {
    let title = t(`pathname:${NavigationRoutes.TransactionCategoriesCreate}`);
    if (transactionCategory) {
      title = t(`pathname:${NavigationRoutes.TransactionCategoriesView}`, {
        extra: transactionCategory.name,
      });
    }
    titleRef.current?.setTitle(title);
  }, [transactionCategory, t]);

  return (
    <UniskaiFormTemplate
      formProps={{
        defaultEditable: !transactionCategory,
        defaultValues,
        resolver: yupResolver(TransactionCategoryFormSchema()),
        onSubmit: props.onSubmit as any,
      }}
      customs={[
        {
          type: 'delete',
          loading: deleteMutate.isLoading,
          onClick: () => onDelete(transactionCategory?.id),
          show: permission.delete,
        },
        {
          type: 'edit',
          show: permission.update,
        },
      ]}
      data={transactionCategory}
      query={props.query}
      titleRef={titleRef}
      saveMutation={props.saveMutation}
    >
      <HalfGridContainer>
        <Input
          type="text"
          name="name"
          label={t('common:name')}
          placeholder={t('common:name')}
          required
        />
      </HalfGridContainer>
    </UniskaiFormTemplate>
  );
}
